import { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { depascalize, SortOrder, listFiltersService, ListTableSort } from "sprint-common-web";

import { ContentSource } from "../../../common/types/ContentSource";

export const sourceOptions = [ContentSource.Sprint, ContentSource.Buzz].map((source) => ({
    key: source,
    text: depascalize(ContentSource[source])
}));

export interface TvSeriesListingFilters {
    page: number;
    pageSize: number;
    sortBy: string;
    sortOrder: SortOrder;
    title?: string;
    orphanedOnly?: boolean;
    includeMerged?: boolean;
    sources?: ContentSource[];
}

const defaultFilters: TvSeriesListingFilters = {
    page: 1,
    pageSize: 50,
    sortBy: "title",
    sortOrder: "asc",
    title: "",
    includeMerged: false,
    sources: undefined
};

export const useTvSeriesListingFilters = () => {
    const filtersService = useMemo(() => listFiltersService<TvSeriesListingFilters>(defaultFilters), []);
    const location = useLocation();
    const history = useHistory();

    const [filters, setFilters] = useState<TvSeriesListingFilters>(() =>
        filtersService.getSavedFilters(location.search)
    );

    const setSort = (sort: ListTableSort) => {
        saveAndSetFilters({ sortBy: sort.by, sortOrder: sort.order, page: 1 });
    };

    const setPage = (page: number) => {
        saveAndSetFilters({ page });
    };

    const setPageSize = (pageSize: number): void => {
        saveAndSetFilters({ pageSize, page: 1 });
    };

    const setTitle = (title?: string): void => {
        saveAndSetFilters({ title });
    };

    const setIncludeMerged = (includeMerged?: boolean): void => {
        saveAndSetFilters({ includeMerged });
    };

    const setSources = (sources?: ContentSource[]): void => {
        saveAndSetFilters({ sources, page: 1 });
    };

    const clearFilters = (): void => {
        saveAndSetFilters(defaultFilters);
    };

    const saveAndSetFilters = (newFilters: Partial<TvSeriesListingFilters>): void => {
        setFilters((prevFilters: TvSeriesListingFilters) => {
            const updatedFilters = { ...prevFilters, ...newFilters };

            filtersService.saveFilters(location.pathname, history, updatedFilters);

            return updatedFilters;
        });
    };

    return {
        filters,
        setSort,
        setPage,
        setPageSize,
        setTitle,
        clearFilters,
        setIncludeMerged,
        setSources
    };
};
